/**
 * Reiter *Geld* eines Vertrags — die Sollstellungen (`charges`, Spec 04a,
 * „Reiter Geld eines Vertrags · die Sollstellungen"). Die Stelle, die Spec 03
 * ausdrücklich leer gelassen hat (`ContractList`s vorheriger Platzhalter).
 *
 * „Sollstellung nachziehen" (`charge-generate`) ist **die primäre Aktion
 * dieses Reiters** — ein Klick öffnet die Rückfrage mit Monatsauswahl, kein
 * eigener Bildschirm. Ein Klick auf eine Zeile öffnet `ChargeSheet` zum
 * Korrigieren.
 */

import { PenLine } from 'lucide-react'
import { useState } from 'react'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { EmptyState } from '@/components/states/EmptyState'
import { ErrorState } from '@/components/states/ErrorState'
import { SkeletonRows } from '@/components/states/Skeleton'
import { ApiError } from '@/lib/api'
import { formatDate } from '@/lib/date'
import type { ContractCharge, useContractCharges } from '@/features/billing/api'
import { useGenerateCharges } from '@/features/billing/api'
import { ChargeSheet } from '@/features/billing/ChargeSheet'
import { FIELD_CLASS } from '@/components/FormField'
import { statusBorderClass } from '@/features/billing/status'

interface ChargesListProps {
  readonly query: ReturnType<typeof useContractCharges>
  readonly contractId: string
}

export function ChargesList({ query, contractId }: ChargesListProps) {
  const [selectedCharge, setSelectedCharge] = useState<ContractCharge | null>(null)
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const accessDenied = query.error instanceof ApiError && (query.error.isForbidden || query.error.isUnauthenticated)
  const charges = [...new Map(query.data?.pages.flatMap((page) => page.items).map((charge) => [charge.charge_id, charge])).values()]

  function openCharge(charge: ContractCharge) {
    setSelectedCharge(charge)
    setIsSheetOpen(true)
  }

  return (
    <div data-testid="charges" className="flex flex-col">
      <div className="flex justify-end px-(--page-x) py-2">
        <GenerateChargeButton contractId={contractId} />
      </div>

      {query.isPending && <SkeletonRows testId="charges-loading" rows={4} />}

      {query.isError && (!query.isFetchNextPageError || accessDenied) && (
        <ErrorState
          testId="charges-error"
          retryTestId="charges-retry"
          error={query.error}
          onRetry={() => {
            void query.refetch()
          }}
        />
      )}

      {query.isSuccess && charges.length === 0 && (
        <EmptyState testId="charges-empty" title="Noch keine Sollstellung." hint="Sollstellung nachziehen." />
      )}

      {!accessDenied && charges.length > 0 && (
        <ul>
          {charges.map((charge) => (
            <ChargeRow
              key={charge.charge_id}
              charge={charge}
              onClick={() => {
                openCharge(charge)
              }}
            />
          ))}
        </ul>
      )}

      {query.isFetchNextPageError && !accessDenied && (
        <ErrorState testId="charges-more-error" retryTestId="charges-more-retry" error={query.error} onRetry={() => { void query.fetchNextPage() }} />
      )}
      {query.hasNextPage && !query.isFetchNextPageError && !accessDenied && (
        <button type="button" data-testid="charges-more" disabled={query.isFetchingNextPage} onClick={() => { void query.fetchNextPage() }} className="border-border text-text border px-3">
          {query.isFetchingNextPage ? 'Lädt …' : 'Mehr laden'}
        </button>
      )}

      <ChargeSheet open={isSheetOpen} onOpenChange={setIsSheetOpen} charge={selectedCharge} />
    </div>
  )
}

/** Exportiert für die Bildebene (`ChargesList.bild.test.tsx`) — dieselbe
 * Zeile, die die Liste rendert, ohne Abfrage und ohne Router. */
export function ChargeRow({ charge, onClick }: { readonly charge: ContractCharge; readonly onClick: () => void }) {
  return (
    <li className={`border-b-border border-b last:border-b-0 ${statusBorderClass(charge.status.value)}`}>
      <button
        type="button"
        data-testid={`charge-row-${charge.charge_id}`}
        onClick={onClick}
        className="hover:bg-bg flex min-h-(--row-h) w-full flex-col gap-1 py-2 pr-(--page-x) pl-3 text-left transition-colors duration-(--duration-fast) ease-(--ease-out) md:min-h-(--row-h) md:flex-row md:items-center md:gap-3 md:py-1"
      >
        {/* Ein Textknoten statt zweier Flex-Kinder: `period_display` und
         * `kind.label` fließen zusammen und brechen bei Bedarf um, statt sich
         * mit der nächsten Spalte zu überlagern (gefunden bei den Aufnahmen
         * — „August 2026 · Nebenkosten" sprengte die feste Breite). Breit
         * genug für „September 2026 · Nebenkosten", den längsten Fall. */}
        <span className="text-body text-text md:w-56">
          <span className="font-medium">{charge.period_display}</span>{' '}
          <span className="text-label text-muted">· {charge.kind.label}</span>
        </span>
        <span className="text-label text-muted whitespace-nowrap md:w-32">fällig {formatDate(charge.due_on)}</span>
        {/* Die drei Beträge tragen ihre Bedeutung mit. Ohne sie standen sie
         * als „920,00 € / 0,00 € / 920,00 €" nebeneinander — ohne Kopfzeile,
         * ohne `sr-only`, ohne `aria-label`; wer die Spaltenordnung nicht
         * kennt, liest drei Zahlen (`design-reviewer` 2026-09-07, Befund N6).
         * iOS beschriftet „gezahlt …" und „offen …" sichtbar; hier bleiben die
         * Beschriftungen für Sehende in der Spaltenbreite verborgen und
         * erreichen den Screenreader — die Liste hat keine Kopfzeile, an der
         * sie hängen könnten. */}
        <span data-numeric className="text-body text-text md:w-24">
          <span className="sr-only">berechnet </span>
          {charge.charged_display}
        </span>
        <span data-numeric className="text-label text-muted md:w-24">
          <span className="sr-only">gezahlt </span>
          {charge.allocated_display}
        </span>
        <span data-numeric className="text-body text-text md:w-24 font-medium">
          <span className="sr-only">offen </span>
          {charge.open_display}
        </span>
        <span className="flex items-center gap-1.5 self-start md:self-auto">
          <span className="border-border text-label text-muted rounded-card border px-2 py-0.5">
            {charge.status.label}
          </span>
          {charge.adjusted_manually && (
            <span
              data-testid={`charge-adjusted-${charge.charge_id}`}
              role="img"
              aria-label="Von Hand angepasst"
              title="Von Hand angepasst"
              className="inline-flex shrink-0"
            >
              <PenLine aria-hidden="true" className="text-muted size-3.5" />
            </span>
          )}
        </span>
      </button>
    </li>
  )
}

/** „Sollstellung nachziehen" — Auslöser plus Rückfrage mit Monatsauswahl in
 * einem, wie `ArchiveControls`. `<input type="month">` ist die native
 * Monatsauswahl (AK-295-Gegenstück für `charge-generate-month`): der Browser
 * lässt keinen Tag zu, ohne dass der Client selbst etwas verbieten müsste. */
function GenerateChargeButton({ contractId }: { readonly contractId: string }) {
  const generateMutation = useGenerateCharges()

  const [isOpen, setIsOpen] = useState(false)
  const [month, setMonth] = useState('')
  const [generalError, setGeneralError] = useState<string | undefined>(undefined)
  const [resultMessage, setResultMessage] = useState<string | undefined>(undefined)

  function openDialog() {
    setMonth('')
    setGeneralError(undefined)
    setResultMessage(undefined)
    setIsOpen(true)
  }

  async function handleConfirm() {
    if (month === '') return
    setGeneralError(undefined)
    try {
      const result = await generateMutation.mutateAsync({ contractId, periodMonth: month })
      setIsOpen(false)
      setResultMessage(result.created > 0 ? `${String(result.created)} Sollstellungen erzeugt.` : 'Nichts nachzuziehen.')
    } catch (error) {
      setGeneralError(error instanceof ApiError ? error.message : 'Die Verbindung zum Server ist unterbrochen.')
    }
  }

  return (
    <>
      <button
        type="button"
        data-testid="charge-generate"
        onClick={openDialog}
        className="bg-accent rounded-card text-body px-4 text-accent-on transition-opacity duration-(--duration-fast) ease-(--ease-out)"
      >
        Sollstellung nachziehen
      </button>
      {resultMessage !== undefined && (
        <p data-testid="charge-generate-result" role="status" className="text-label text-muted self-center pr-2">
          {resultMessage}
        </p>
      )}

      <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-section font-(--font-weight-section) text-text">
              Sollstellung nachziehen
            </AlertDialogTitle>
            <AlertDialogDescription className="text-label text-muted">
              Wähle den Monat, für den eine fehlende Sollstellung erzeugt werden soll.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <label className="text-label text-muted flex flex-col gap-1" htmlFor="charge-generate-month">
            Monat
            <input
              id="charge-generate-month"
              data-testid="charge-generate-month"
              type="month"
              value={month}
              onChange={(event) => {
                setMonth(event.target.value)
              }}
              className={`text-text ${FIELD_CLASS}`}
            />
          </label>

          {generalError !== undefined && (
            <p role="alert" className="text-label text-status-overdue">
              {generalError}
            </p>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel data-testid="charge-generate-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              data-testid="charge-generate-confirm"
              disabled={month === '' || generateMutation.isPending}
              onClick={() => void handleConfirm()}
            >
              Nachziehen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
